import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove } from "@dnd-kit/sortable";
import { ColumnDef, getCoreRowModel, Header, TableOptions, TableState, useReactTable } from "@tanstack/react-table";
import { useVirtualizer, Virtualizer } from "@tanstack/react-virtual";

import { InfinitePaginatedPromise } from "@/types/InfiniteQuery.type";
import { UseTableConfigReturn } from "@/hooks/table/useTableConfig";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import DraggedColumnHeader from "@/components/molecules/table/DraggedColumnHeader";
import { VirtualTablePaddings } from "@/components/organisms/table/table.utils";
import TableBody from "@/components/organisms/table/TableBody";
import TableHead from "@/components/organisms/table/TableHead";

const defaultColumnSettings = {
  size: 300,
  minSize: 150,
  maxSize: 500,
};

export enum TableType {
  LISTS = "LISTS",
  COMPANIES = "COMPANIES",
}

type TableProps<T extends { id: string }> = {
  data: T[];
  tableType?: TableType;
  isLoading: boolean;
  loadingRowCount?: number;
  showHeaderWhileLoading?: boolean;
  loadMore?: () => InfinitePaginatedPromise<T>;
  hasMoreData?: boolean;
  isFetchingNextPage?: boolean;
  tableOptions?: Partial<TableOptions<T>>;
  tableState?: Partial<TableState>;
  canReorderColumns?: boolean;
  canReorderRows?: boolean;
  hasContextMenu?: boolean;
  onRename?: (id: string, newValue: string) => void;
  canSelectRows?: boolean;
  allColumns: ColumnDef<T>[];
  isEndOfData?: boolean;
  endOfDataComponent?: ReactNode;
  onDetailClicked?: (rowId: string) => void;
  activeDetailId: string | null;
} & UseTableConfigReturn;

function handleDragEnd(
  event: DragEndEvent,
  canReorderRows: boolean,
  canReorderColumns: boolean,
  columnOrder: string[],
  updateColumnOrder: (columnOrder: string[]) => void,
  rowOrder: string[],
  updateRowOrder: (rowOrder: string[]) => void,
) {
  const { active, over } = event;
  if (!active || !over) return;

  const activeId = active.id as string;
  const overId = over.id as string;

  // Handle Column Drag
  if (canReorderColumns && activeId.startsWith("column-") && overId.startsWith("column-")) {
    const activeColumnId = activeId.replace("column-", "");
    const overColumnId = overId.replace("column-", "");
    const activeIndex = columnOrder.indexOf(activeColumnId);
    const overIndex = columnOrder.indexOf(overColumnId);

    const isLeftDrag = activeIndex > overIndex ? 1 : 0;

    // Prevent moving the first column (index 0)
    if (activeIndex === 0 || overIndex === 0) return;

    const newOrder = arrayMove(columnOrder, activeIndex, overIndex + isLeftDrag);
    updateColumnOrder(newOrder);
  }

  // Handle Row Drag
  else if (canReorderRows && activeId.startsWith("row-") && overId.startsWith("row-")) {
    const activeRowId = activeId.replace("row-", "");
    const overRowId = overId.replace("row-", "");
    const activeIndex = rowOrder.indexOf(activeRowId);
    const overIndex = rowOrder.indexOf(overRowId);

    if (activeIndex === -1 || overIndex === -1) return;

    const newRowOrder = arrayMove(rowOrder, activeIndex, overIndex);
    updateRowOrder(newRowOrder);
  }
}

function calculateVirtualPaddings(columnVirtualizer: Virtualizer<HTMLDivElement, Element>): VirtualTablePaddings {
  const virtualColumns = columnVirtualizer.getVirtualItems();

  let virtualPaddingLeft: number | undefined;
  let virtualPaddingRight: number | undefined;

  if (columnVirtualizer && virtualColumns?.length) {
    virtualPaddingLeft = virtualColumns[0]?.start ?? 0;
    virtualPaddingRight = columnVirtualizer.getTotalSize() - (virtualColumns[virtualColumns.length - 1]?.end ?? 0);
  }

  return { left: virtualPaddingLeft, right: virtualPaddingRight };
}

function dispatchRowOrderUpdate<
  T extends {
    id: string;
  },
>(rowOrder: string[], shownData: T[], updateRowOrder: (rowOrder: string[]) => void) {
  let updatedRowOrder = [...rowOrder];

  // When data updates (e.g. new page) we need to add missing items to our row order
  shownData.forEach((dataItem) => {
    if (!updatedRowOrder.includes(dataItem.id)) {
      updatedRowOrder.push(dataItem.id);
    }
  });

  // The entry was remove from data (e.g. new search) so we must remove it from our order
  const dataItemIds = shownData.map((dataItem) => dataItem.id);
  updatedRowOrder.forEach((rowId) => {
    if (!dataItemIds.includes(rowId)) {
      updatedRowOrder = updatedRowOrder.splice(updatedRowOrder.indexOf(rowId), 1);
    }
  });

  updateRowOrder(updatedRowOrder);
}

const Table = <T extends { id: string }>({
  data,
  tableType = TableType.COMPANIES,
  isLoading,
  loadingRowCount = 25,
  showHeaderWhileLoading = false,
  loadMore,
  hasMoreData,
  isFetchingNextPage,
  tableOptions = {},
  canReorderColumns = false,
  canReorderRows = false,
  hasContextMenu = false,
  onRename,
  canSelectRows = true,
  columnVisibility,
  allColumns,
  tableState,
  columnOrder,
  updateColumnOrder,
  rowOrder,
  updateRowOrder,
  hideRows,
  hiddenRows,
  setRowSelection,
  rowSelection,
  isEndOfData = false,
  endOfDataComponent,
  onDetailClicked,
  activeDetailId,
}: TableProps<T>) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useInfiniteScroll({
    hasNextPage: !!hasMoreData,
    fetchNextPage: loadMore,
    containerRef: tableContainerRef,
  });

  const [activeColumn, setActiveColumn] = useState<string | null>(null);
  const [draggedHeader, setDraggedHeader] = useState<Header<T, unknown> | undefined>();
  const [draggedOverColumnId, setDraggedOverColumnId] = useState<string | null>(null);
  const [currentRowSelection, setCurrentRowSelection] = useState<Record<string, boolean>>({});

  const sensors = useSensors(useSensor(MouseSensor, {}));

  const shownData = useMemo(() => {
    return data.filter((dataItem) => !hiddenRows.includes(dataItem.id));
  }, [data, hiddenRows]);

  // Adjust rowOrder so that the correct items are contained
  useEffect(() => dispatchRowOrderUpdate(rowOrder, shownData, updateRowOrder), [shownData]);

  // Used for row reordering
  /*const orderedData = useMemo(() => {
    return rowOrder
      .map((rowId) => shownData.find((dataItem) => dataItem.id === rowId))
      .filter((item): item is T => item !== undefined);
  }, [rowOrder, shownData]);*/

  useEffect(() => {
    setRowSelection(currentRowSelection);
  }, [currentRowSelection]);

  // Configure the table using useReactTable with external options
  const table = useReactTable<T>({
    data: shownData,
    columns: allColumns,
    defaultColumn: defaultColumnSettings,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
    onRowSelectionChange: setCurrentRowSelection,
    state: {
      columnVisibility,
      columnOrder,
      rowSelection,
      ...tableState,
    },
    ...tableOptions,
  });

  const visibleColumnCount = useMemo(() => {
    return Object.entries(columnVisibility).filter(([, value]) => value).length;
  }, [columnVisibility]);

  const columnSizes = useMemo(() => {
    return table.getVisibleLeafColumns().map((column) => column.getSize());
  }, [table.getVisibleLeafColumns()]);

  const rowVirtualizer = useVirtualizer({
    count: shownData.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 36,
    overscan: 20,
  });

  const columnVirtualizer = useVirtualizer({
    count: visibleColumnCount,
    estimateSize: (index) => columnSizes[index],
    getScrollElement: () => tableContainerRef.current,
    horizontal: true,
    overscan: 35,
  });

  const virtualPaddings: VirtualTablePaddings = useMemo(
    () => calculateVirtualPaddings(columnVirtualizer),
    [columnVirtualizer.getVirtualItems()],
  );

  const _handleDragStart = (event: DragEndEvent) => {
    const id = event.active.id as string;
    const activeColumnId = id.replace("column-", "");
    const activeCol = table.getColumn(activeColumnId);

    const headerGroups = table.getHeaderGroups();
    const activeHeader = headerGroups
      .flatMap((group) => group.headers)
      .find((header) => header.column.id === activeCol!.id);

    setDraggedHeader(activeHeader);

    setActiveColumn(event.active.id.toString());
  };

  const _handleDragEnd = (event: DragEndEvent) => {
    setActiveColumn(null);
    setDraggedOverColumnId(null);
    handleDragEnd(event, canReorderRows, canReorderColumns, columnOrder, updateColumnOrder, rowOrder, updateRowOrder);
  };

  const _handleDragOver = (event: DragEndEvent) => {
    const draggedOverColumn = event.over?.id as string;
    setDraggedOverColumnId(draggedOverColumn.replace("column-", ""));
  };

  return (
    <>
      <div ref={tableContainerRef} className={"relative w-full overflow-auto rounded-lg border border-secondary"}>
        <table className={"grid border-separate border-spacing-0 pb-4"}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={canReorderColumns ? [restrictToHorizontalAxis, restrictToParentElement] : []}
            onDragStart={_handleDragStart}
            onDragOver={_handleDragOver}
            onDragEnd={_handleDragEnd}
            sensors={sensors}
            autoScroll={{
              acceleration: 100,
            }}
          >
            <TableHead
              table={table}
              columnVirtualizer={columnVirtualizer}
              rowVirtualizer={rowVirtualizer}
              virtualPaddings={virtualPaddings}
              columnVisibility={columnVisibility}
              canReorderColumns={canReorderColumns}
              isLoading={isLoading}
              showHeaderWhileLoading={showHeaderWhileLoading}
            />
            {/* Ghost Element for Column Dragging */}
            <DragOverlay modifiers={[restrictToParentElement]}>
              {activeColumn ? <DraggedColumnHeader header={draggedHeader} /> : null}
            </DragOverlay>
          </DndContext>

          <TableBody
            table={table}
            rowVirtualizer={rowVirtualizer}
            columnVirtualizer={columnVirtualizer}
            virtualPaddings={virtualPaddings}
            canReorderRows={canReorderRows}
            tableType={tableType}
            hasContextMenu={hasContextMenu}
            onRename={onRename}
            canSelectRows={canSelectRows}
            onRemoveRows={hideRows}
            isFetchingNextPage={isFetchingNextPage}
            loadingRowCount={loadingRowCount}
            isLoading={isLoading}
            draggedOverColumnId={draggedOverColumnId}
            onDetailClicked={onDetailClicked}
            activeDetailId={activeDetailId}
          />
        </table>
        <div ref={observerRef} />
        {isEndOfData && endOfDataComponent}
      </div>
    </>
  );
};

export default Table;
